import { FileCategoryType, IFile, INumberedDocumentView } from '../../../api-client/autogenerated';
import {
  collectDocumentFiles,
  isListedInSupportingItems,
} from '../../supporting-documents/supporting-files';
import { ChatContextScope } from '../core/types';
import { ReviewKind } from './review-visibility';
import { ReviewDocumentFacts } from './review-prompts';

/** The Query Tool takes at most this many file ids in a scope. */
export const MAX_REVIEW_FILES = 200;

/** The sets an RFI is answered from; the scope is a union, so the RFI's own files stay in. */
export const RFI_DOC_TYPES = ['Drawings', 'Specifications'];

/**
 * Files that are not the contractor's submission: the Architect's own returned file and the
 * consultants' uploads and markups (the AI reviews what the contractor sent), the final executed
 * and deliverable copies the Architect produces, and thumbnails, which carry no content.
 */
const LEFT_OUT: FileCategoryType[] = [
  FileCategoryType.DocumentArchitectApproved,
  FileCategoryType.DocumentConsultantUpload,
  FileCategoryType.DocumentFinalExecuted,
  FileCategoryType.DocumentFinalDeliverable,
  FileCategoryType.Thumbnail,
];

const byNewest = (a: IFile, b: IFile) =>
  (b.createdOn ? Date.parse(b.createdOn) : 0) - (a.createdOn ? Date.parse(a.createdOn) : 0);

export interface ReviewFiles {
  /** Every file sent as context, newest first. */
  files: IFile[];
  /** Those among them that came with the document itself, not the spec or drawings section. */
  ownFileIds: string[];
  scope: ChatContextScope;
  /** More files than the Query Tool accepts, so the oldest were left out. */
  truncated: boolean;
  /** Whether there is anything to review: a submittal needs its own file; an RFI has the full sets. */
  canRun: boolean;
}

/**
 * The files a review sends as context (plan §4.2): the document's own files as its Supporting Items
 * card shows them, minus the Architect's and consultants' files. A submittal adds its specification
 * section; an RFI adds the project's whole Drawings and Specifications sets.
 */
export function collectReviewFiles(document: INumberedDocumentView, kind: ReviewKind): ReviewFiles {
  const seen = new Set<string>();
  const all = collectDocumentFiles(document)
    .filter(isListedInSupportingItems)
    .filter((f) => !LEFT_OUT.includes(f.category))
    .filter((f) => {
      if (!f.id || seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    })
    .sort(byNewest);
  const files = all.slice(0, MAX_REVIEW_FILES);
  const sectionIds = new Set(
    [document.drawingsSectionFile?.id, document.submittalSectionFile?.id].filter(
      (id): id is string => !!id,
    ),
  );
  const ownFileIds = files
    .filter(
      (f) =>
        !sectionIds.has(f.id) &&
        f.category !== FileCategoryType.SpecificationSection &&
        f.category !== FileCategoryType.DrawingsSection,
    )
    .map((f) => f.id);
  return {
    files,
    ownFileIds,
    scope: {
      doc_types: kind === 'rfi' ? [...RFI_DOC_TYPES] : [],
      file_ids: files.map((f) => f.id),
    },
    truncated: all.length > files.length,
    canRun: kind === 'rfi' || ownFileIds.length > 0,
  };
}

/** What the prompts say about the document itself. */
export const documentFacts = (
  document: INumberedDocumentView,
  label: string,
): ReviewDocumentFacts => ({
  label,
  title: document.title,
  section: document.submittalSection,
  paragraph: document.submittalParagraphNumber,
  proposedSubstitution: document.proposedSubstitution,
  description: document.description,
  request: document.request,
});
